import { t } from '@lingui/macro';

import { Box, Flex, Image, Text } from '@rover/kibble/core';
import { Props as BoxProps } from '@rover/kibble/core/Box/Box';
import useTheme from '@rover/kibble/themes/useTheme';
import type { Image as ImageType } from '@rover/rsdk/src/apiClient/latest/model/image';

import { getAlt, getAriaLabel, getImageContainerSize, getSourceSetProps } from './utils';

type AnimatedBoxProps = Omit<BoxProps, 'onClick'> & { onClick: () => void };
const AnimatedBox = ({ children, ...props }: AnimatedBoxProps): JSX.Element => {
  const onKeyDown = (event): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      props.onClick();
    }
  };
  return (
    <Box
      {...props}
      webProps={{
        onKeyDown,
        tabIndex: 0,
      }}
      role="button"
      sx={{
        ...props.sx,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          cursor: 'pointer',
          transform: 'scale(1.03)',
        },
      }}
    >
      {children}
    </Box>
  );
};

type OverlayProps = {
  total: number;
};

const Overlay = ({ total }: OverlayProps): JSX.Element => {
  const theme = useTheme();
  return (
    <Box position="absolute" width="100%" height="100%" top="0" py="1x" pr="2x">
      <Box
        display="flex"
        sx={{ backgroundColor: theme.background.overlay }}
        height="100%"
        borderRadius="primary"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
      >
        <Text textColor="primary_inverse" size="200" fontWeight="semibold">
          {total}
        </Text>
        <Text textColor="primary_inverse" size="200" fontWeight="semibold">
          {t`Photos`}
        </Text>
        <Text textColor="primary_inverse" size="100" fontWeight="regular">
          {t`View All`}
        </Text>
      </Box>
    </Box>
  );
};

type MainImageProps = {
  image: ImageType;
  onClick: () => void;
  totalImageCount: number;
  description: string;
  ariaLabel: string;
};

type MainImageMobileProps = MainImageProps;
/**
 * Renders an image scaled to fit the height of its container, with a blurred copy of the image behind it.
 * If the image does not fill the width of its container, the blurred image will be visible on the sides behind it.
 * If the image exceeds the width of the container, the sides will be cropped and no blurred image will be visible.
 */
const MainImageMobile = ({
  image,
  onClick,
  totalImageCount,
  description,
  ariaLabel,
}: MainImageMobileProps): JSX.Element => {
  const blurRadius = '5px';
  const imageProps = {
    webProps: {
      ...getSourceSetProps(image, 1, totalImageCount),
      eager: true,
    },
    description,
    'aria-label': ariaLabel,
  };
  const height = '220px';

  return (
    <AnimatedBox
      flex="1"
      display={['flex', 'none']}
      justifyContent="center"
      alignItems="center"
      onClick={onClick}
      sx={{ overflow: 'hidden', position: 'relative' }}
      borderRadius="0x"
      height={height}
      marginTop="0x"
    >
      <Box
        sx={{
          position: 'absolute',
          zIndex: -1,
          // blurRadius * 2 still leaves blurry white space on the edges; using blurRadius * 4 to be safe
          width: `calc(100% + (${blurRadius} * 4))`,
          height: `calc(100% + (${blurRadius} * 4))`,
          top: `calc(-1 * ${blurRadius} * 2)`,
          filter: `blur(${blurRadius}) brightness(0.85)`,
        }}
      >
        <Image width="100%" height="100%" resizeMethod="cover" {...imageProps} />
      </Box>
      <Image width="auto" height={height} resizeMethod="contain" {...imageProps} />
    </AnimatedBox>
  );
};

type MainImageDesktopProps = MainImageProps;
const MainImageDesktop = ({
  image,
  onClick,
  totalImageCount,
  description,
  ariaLabel,
}: MainImageDesktopProps): JSX.Element => (
  <AnimatedBox
    display={['none', 'flex']}
    flex="1"
    justifyContent="center"
    alignItems="center"
    onClick={onClick}
    height="280px"
    marginTop="1x"
    borderRadius="primary"
    marginRight="1x"
  >
    <Image
      height="100%"
      width="100%"
      webProps={{
        ...getSourceSetProps(image, 1, totalImageCount),
        eager: true,
      }}
      description={description}
      aria-label={ariaLabel}
      borderRadius="primary"
    />
  </AnimatedBox>
);

type TrainerPhotoGalleryProps = {
  images: ImageType[];
  onImageClick: (index: number) => void;
  count: number;
};

const TrainerPhotoGallery = ({
  images,
  onImageClick,
  count,
}: TrainerPhotoGalleryProps): JSX.Element => {
  const firstImage = images[0];
  const otherImages = images.slice(1, 5);
  const totalImageCount = images.length;

  const overlay = images.length > 5;
  const mainImageProps: MainImageProps = {
    image: firstImage,
    onClick: () => onImageClick(1),
    totalImageCount: images.length,
    description: getAlt(1),
    ariaLabel: getAriaLabel(1),
  };

  return (
    <Flex flexDirection="row" width="100%" data-testid="photo-gallery">
      <MainImageMobile {...mainImageProps} />
      <MainImageDesktop {...mainImageProps} />
      {otherImages.length > 0 && (
        <Flex
          display={['none', 'flex']}
          width={['100%', '50%', '66%', '50%']}
          flexWrap="wrap"
          height="288px"
        >
          {otherImages.map((image, otherIndex) => (
            <AnimatedBox
              {...getImageContainerSize(otherIndex + 2, totalImageCount)}
              p="1x"
              key={otherIndex}
              position="relative"
              onClick={() => onImageClick(otherIndex + 2)}
            >
              <Image
                height="100%"
                width="100%"
                webProps={{
                  ...getSourceSetProps(image, otherIndex + 2, totalImageCount),
                  eager: true,
                }}
                description={getAlt(otherIndex + 2)}
                borderRadius="primary"
              />
              {overlay && otherIndex === 3 && <Overlay total={count} />}
            </AnimatedBox>
          ))}
        </Flex>
      )}
    </Flex>
  );
};

export default TrainerPhotoGallery;
